import React, { useState } from 'react';
import { DEPARTMENTS, DISTRO_OPTIONS } from './constants';
import AgreementSummary from './AgreementSummary';
import UsageTab from './UsageTab';

/**
 * Analytics — two independent sections, each with the filters that drive it
 * directly above it:
 *   1. Token usage: LLM tokens per day, by tier (UsageTab owns its filters —
 *      agent and date range — since the agreement filters don't apply to it).
 *   2. Routing accuracy: how well the AI's routing agrees with human labels.
 *
 * Its filters are its own, not shared with the Decisions tab: the agreement
 * endpoint filters server-side on distro / department / date, while the
 * Decisions table also has a local text search the endpoint cannot apply, so
 * sharing them would let the rate silently describe a different set of rows
 * than the table.
 *
 * No refresh plumbing is needed after a label write: the portal renders one
 * tab at a time, so this tab mounts — and both sections fetch — fresh every
 * time it is opened.
 */
export default function AnalyticsTab() {
  const [distro, setDistro] = useState('');
  const [department, setDepartment] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const hasActiveFilters = Boolean(distro || department || fromDate || toDate);

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-boss-text">Token usage</h2>
          <p className="text-sm text-boss-text-secondary mt-0.5">
            LLM tokens the dispatcher used per day, by tier.
          </p>
        </div>
        <UsageTab />
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-boss-text">Routing accuracy</h2>
          <p className="text-sm text-boss-text-secondary mt-0.5">
            How often the AI's routing matches what a human labeller said it should have been.
          </p>
        </div>

        <div className="glass-card p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label htmlFor="analytics-filter-distro" className="block text-xs text-boss-text-secondary mb-1">
                Distro
              </label>
              <select
                id="analytics-filter-distro"
                className="boss-input h-10 px-2 w-40"
                value={distro}
                onChange={(e) => setDistro(e.target.value)}
              >
                {DISTRO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="analytics-filter-department" className="block text-xs text-boss-text-secondary mb-1">
                Department
              </label>
              <select
                id="analytics-filter-department"
                className="boss-input h-10 px-2 w-44"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                <option value="">All departments</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="analytics-filter-from" className="block text-xs text-boss-text-secondary mb-1">
                From
              </label>
              <input
                id="analytics-filter-from"
                type="date"
                className="boss-input h-10 px-2"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="analytics-filter-to" className="block text-xs text-boss-text-secondary mb-1">
                To
              </label>
              <input
                id="analytics-filter-to"
                type="date"
                className="boss-input h-10 px-2"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => {
                  setDistro('');
                  setDepartment('');
                  setFromDate('');
                  setToDate('');
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <AgreementSummary
          distro={distro}
          department={department}
          fromDate={fromDate}
          toDate={toDate}
        />
      </section>
    </div>
  );
}
